/**
 * Validates that a string is safe to use as a target for `window.location.href`
 * without enabling an open-redirect or `javascript:` URI XSS.
 *
 * A value is considered safe only when it is a same-origin absolute path:
 *   - starts with a single '/'
 *   - is not protocol-relative ('//evil.com' or '/\\evil.com')
 *   - does not embed a URI scheme (e.g. 'javascript:', 'data:', 'http:')
 *
 * The value is decoded once before checking to defeat encoded bypasses such as
 * '%2F%2Fevil.com'.
 */
export const isSafeInternalPath = (value: string | null | undefined): value is string => {
  if (!value) {
    return false
  }

  let decoded: string
  try {
    decoded = decodeURIComponent(value)
  } catch {
    return false
  }

  // Must be an absolute path on the current origin.
  if (decoded[0] !== '/') {
    return false
  }

  // Reject protocol-relative URLs and backslash variants.
  if (decoded[1] === '/' || decoded[1] === '\\') {
    return false
  }

  // Reject any embedded scheme (e.g. '/foo:bar' is fine because ':' would appear
  // after a path separator only if the path itself contains it; a scheme would
  // appear before the first '/' which we already excluded). As an extra guard,
  // reject control characters and whitespace that browsers may strip.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\s]/.test(decoded)) {
    return false
  }

  return true
}
